import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Row, Col, Divider, Spin, Input, Flex, Tag } from "antd";
import CustomCard from "../../components/CustomCard/CustomCard";
import CustomTable from "../../components/CustomTable/CustomTable";
import BackButton from "../../components/CustomButton/BackButton"; // import BackButton
import { getTransactions } from "../../redux/slices/walletsSlice";
import { formatCurrency, formatDateTime } from "../../helpers/helpers";
import { useLocation } from "react-router-dom";
import useSearchableTable from "../../hooks/useSearchableTable";

const { Text } = Typography;

const WalletDetails = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const walletInfo = location.state?.walletInfo || {};
  const tabIndex = location.state?.tabIndex;
  const { walletDetailedView, loading } = useSelector((state) => state.wallets);
  const { searchText, setSearchText, filteredData } =
    useSearchableTable(walletDetailedView);

  useEffect(() => {
    if (walletInfo.vaAccountId || walletInfo.bankAccId || walletInfo.busEntId) {
      dispatch(getTransactions({
        vaAccountId: walletInfo.vaAccountId,
        busEntId: walletInfo.busEntId,
        bankAccId: walletInfo.bankAccId,
      }));
    }
  }, [dispatch, walletInfo?.vaAccountId, walletInfo?.busEntId, walletInfo?.bankAccId]);

  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "appTransactionId",
      key: "appTransactionId",
    },
    {
      title: "UTR",
      dataIndex: "utr",
      key: "utr",
      render: (v) => v || "-",
    },
    {
      title: "Transaction Date",
      dataIndex: "transactionDate",
      key: "transactionDate",
      render: (text) => formatDateTime(text),
    },
    {
      title: "Particular",
      dataIndex: "purpose",
      key: "purpose",
    },
    {
      title: "Deal Type",
      dataIndex: "dealType",
      key: "dealType",
      render: (value) => value || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => {
        const isDebit = record.transactionType === "debit";
        return (
          <span style={{ color: isDebit ? "red" : "green" }}>
            {isDebit
              ? `- ${formatCurrency(amount)}`
              : `+ ${formatCurrency(amount)}`}
          </span>
        );
      },
    },
    {
      title: "Closing Balance",
      dataIndex: "closingAmount",
      key: "closingAmount",
      render: formatCurrency,
    },
    {
      title: "Created/Requested By",
      dataIndex: "createdBy",
      key: "createdBy",
    },
    {
      title: "Transaction Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
    },
    {
      title: "Comments",
      dataIndex: "remarks",
      key: "remarks",
      render: (v) => v || "-",
    },
  ];

  return (
    <>
      <BackButton />
      <CustomCard>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
            }}
          >
            <Spin />
          </div>
        ) : (
          <>
            <div style={{ padding: "20px 28px" }}>
              <div className="wallet-card">
                <Row gutter={[16, 16]}>
                  {/* ✅ Simplified View for Business Enterprise Wallets */}
                  {tabIndex === 2 ? (
                    <>
                      <Col xs={24} md={12}>
                        <Text strong>Business Enterprise ID:</Text>{" "}
                        <span>{walletInfo.busEntId || "-"}</span>
                      </Col>
                      <Col xs={24} md={12}>
                        <Text strong>Available Balance:</Text>{" "}
                        <Tag color="green" className="balance-tag">
                          {formatCurrency(walletInfo.balance)}
                        </Tag>
                      </Col>
                      <Col xs={24} md={12}>
                        <Text strong>VA Account ID:</Text>{" "}
                        <span>{walletInfo.vaAccountId || "-"}</span>
                      </Col>
                      <Col xs={24} md={12}>
                        <Text strong>Last Active On:</Text>{" "}
                        <span>{formatDateTime(walletInfo.lastActiveOn)}</span>
                      </Col>
                    </>
                  ) : (
                    /* ✅ Full View for other wallet types */
                    <>
                      <Col xs={24} md={12}>
                        <Text strong>Organization ID:</Text>{" "}
                        <span>{walletInfo.orgId || "-"}</span>
                      </Col>
                      <Col xs={24} md={12}>
                        <Text strong>Available Balance:</Text>{" "}
                        <Tag color="green" className="balance-tag">
                          {formatCurrency(walletInfo.balance)}
                        </Tag>
                      </Col>

                      <Col xs={24} md={12}>
                        <Text strong>VA Account ID:</Text>{" "}
                        <span>{walletInfo.vaAccountId || "-"}</span>
                      </Col>
                      <Col xs={24} md={12}>
                        <Text strong>Organization Name:</Text>{" "}
                        <span>{walletInfo.orgName || "-"}</span>
                      </Col>

                      <Col xs={24} md={12}>
                        <Text strong>Last Active On:</Text>{" "}
                        <span>{formatDateTime(walletInfo.lastActiveOn)}</span>
                      </Col>
                      {tabIndex === 1 && (
                        <Col xs={24} md={12}>
                          <Text strong>User Role:</Text>{" "}
                          <span>
                            {walletInfo.isInvestor ? "Investor" : "Business"}
                          </span>
                        </Col>
                      )}
                    </>
                  )}
                </Row>
              </div>
            </div>

            <Divider />

            {/* 🔹 Transactions Table (Bottom Section) */}
            <div className="wallet-table-section">
              <Flex
                justify="flex-end"
                style={{ marginBottom: 16, marginRight: 25 }}
              >
                <Input
                  placeholder="Search"
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 250 }}
                />
              </Flex>

              <CustomTable
                rowKey="appTransactionId"
                columns={columns}
                dataSource={filteredData}
                loading={loading}
              />
            </div>
          </>
        )}
      </CustomCard>
    </>
  );
};

export default WalletDetails;
